import _ from 'lodash';
import { useEffect } from 'react';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { getOrderedPeopleEffort } from '../services/projects.services';
import PeopleEffortIteration from './PeopleEffortIteration';
import InvestmentBreakdown from './InvestmentBreakdown';
import DeliveryColumn from './DeliveryColumn';
import DeliverySummary from './DeliverySummary';
import ProjectColumnEmptyState from './ProjectColumnEmptyState';
import DeliveryBreakdown from './DeliveryBreakdown';
import babyMobile from 'images/baby-mobile.svg';
import { PROJECTS_VIEW_TYPES } from '../projects.consts';
import usePmViewer from 'common/hooks/pm/usePmViewer';
import { LBButton } from '@linearb/buzz';

const SmallBox = styled(Flex)`
  flex-direction: column;
  ${({ height }) => `height:${height ? height : '13rem'};`}
  padding: 1rem;
  border: 1px solid ${getColor('catskillWhite')};
`;

const LargeBox = styled(Flex)`
  margin-left: 2rem;
  width: 20rem;
  height: 100%;
  padding: 1rem;
  border: 1px solid ${getColor('catskillWhite')};
`;

const IterationTooltip = ({
  title,
  totals,
  iteration,
  iterationIndex,
  totalNumberOfPeople,
  chartConfig,
  investmentSummary,
  investmentColorsMap,
  project,
  issuesData,
  contributors,
  deliveryData = [],
  isScrumProject,
  onClickTrendIteration = null
}) => {
  const pmViewer = usePmViewer();

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.HOVER, {
      page: TRACKING.PAGES.PROJECTS,
      hover_type: TRACKING.CLICK.PROJECT_ITERATION_TOOLTIP_OPENED,
      project_name: _.get(project, 'name', ''),
      iteration_name: _.get(iteration, 'name', '')
    });
  }, []);

  if (_.isNull(iterationIndex)) {
    return false;
  }

  const getDataPerIteration = (data, iterationIndex) =>
    data.filter((item, index) => index === iterationIndex);

  const { excluded_users = [] } = iteration;
  const iterationValue = getDataPerIteration(
    deliveryData[0].iterationsTotals,
    iterationIndex
  );

  const isNoActivity = iterationValue[0] === 0;

  const investmentSummaryPerIteration = investmentSummary
    .map(({ category, data = [] }) => {
      const iterationVal = getDataPerIteration(data, iterationIndex);
      if (_.sum(iterationVal) === 0) return false;
      return {
        category,
        value: _.sum(iterationVal),
        data: iterationVal
      };
    })
    .filter(Boolean);

  const issuesDataPerIteration = issuesData.map(
    ({ key, values = [], color }) => {
      const iterationVal = getDataPerIteration(values, iterationIndex);

      return {
        key,
        color,
        value: Math.round(_.sum(iterationVal)),
        iteration: []
      };
    }
  );

  // const activeUniquePeople = getOrderedPeopleEffort(contributors);
  const excludedUniquePeople = getOrderedPeopleEffort(excluded_users);

  return (
    <Flex
      direction='column'
      padding='medium'
      pt='normal'
      pb='nomral'
      width={isNoActivity ? '23rem' : '50rem'}
    >
      <div className='flex justify-between'>
        {title}
        {!isNoActivity && _.isFunction(onClickTrendIteration) && (
          <div className='flex flex-col items-start'>
            <LBButton onClick={e => onClickTrendIteration(iteration)} size='lg'>
              View Iteration Activity
            </LBButton>
            <div className='text-sm text-muted-foreground'>
              Go to the previous tab for activity details
            </div>
          </div>
        )}
      </div>
      {isNoActivity ? (
        <Padding t='medium'>
          <ProjectColumnEmptyState
            image={babyMobile}
            imageSize='3.5rem'
            message='No activity in this iteration'
          />
        </Padding>
      ) : (
        <>
          <Flex direction='column' pt='normal' pb='small'>
            <Flex>
              {deliveryData.flatMap(
                ({ icon, title, totalText, totalSuffix, iterationsTotals }) => {
                  return (
                    <DeliverySummary
                      key={title}
                      title={totalText}
                      icon={icon}
                      totalValue={iterationsTotals[iterationIndex]}
                      totalSuffix={totalSuffix}
                      isIterationView={true}
                    />
                  );
                }
              )}
            </Flex>
          </Flex>
          <Flex height='100%'>
            <Flex direction='column' width='28rem' height='100%'>
              {isScrumProject ? (
                <SmallBox mb='medium' height='18.5rem'>
                  <DeliveryBreakdown
                    issuesData={issuesDataPerIteration}
                    totals={totals}
                    project={project}
                    displayType={PROJECTS_VIEW_TYPES.ITERATION_VIEW}
                  />
                </SmallBox>
              ) : (
                <SmallBox mb='medium'>
                  <Text size='medium' mb='1.5rem'>
                    {chartConfig.pieTitle || chartConfig.title}
                  </Text>
                  <DeliveryColumn
                    displayType={PROJECTS_VIEW_TYPES.ITERATION_VIEW}
                    issuesData={issuesDataPerIteration}
                    chartConfig={chartConfig}
                    project={project}
                  />
                </SmallBox>
              )}

              <SmallBox>
                <Text size='medium' mb='1.5rem'>
                  Average investment per {pmViewer.issueType.single}
                </Text>
                <InvestmentBreakdown
                  displayType={PROJECTS_VIEW_TYPES.ITERATION_VIEW}
                  investmentSummary={investmentSummaryPerIteration}
                  investmentColorsMap={investmentColorsMap}
                />
              </SmallBox>
            </Flex>
            <LargeBox>
              <PeopleEffortIteration
                project={{
                  avg_contributors: contributors.length
                }}
                peopleEffortAvg={contributors.length}
                totalNumberOfPeople={totalNumberOfPeople}
                activeUniquePeople={contributors}
                excludedUniquePeople={excludedUniquePeople}
                isScrumProject={isScrumProject}
              />
            </LargeBox>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default IterationTooltip;
